import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { gsap } from 'gsap';
import { useBookingModal } from '../contexts/BookingModalContext';

export const BookingModal: React.FC = () => {
    const { isOpen, closeModal } = useBookingModal();
    const overlayRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle animations
    useEffect(() => {
        if (!overlayRef.current || !modalRef.current) return;

        const overlay = overlayRef.current;
        const modal = modalRef.current;

        if (isOpen) {
            // Prevent body scroll
            document.body.style.overflow = 'hidden';

            // Open animation
            gsap.set(overlay, { opacity: 0, pointerEvents: 'auto' });
            gsap.set(modal, { opacity: 0, scale: 0.9, y: 20 });

            gsap.to(overlay, {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            });

            gsap.to(modal, {
                opacity: 1,
                scale: 1,
                y: 0,
                duration: 0.4,
                ease: 'back.out(1.7)',
                delay: 0.1
            });
        } else {
            // Close animation
            gsap.to(modal, {
                opacity: 0,
                scale: 0.95,
                y: 10,
                duration: 0.25,
                ease: 'power2.in'
            });

            gsap.to(overlay, {
                opacity: 0,
                duration: 0.3,
                ease: 'power2.in',
                delay: 0.1,
                onComplete: () => {
                    gsap.set(overlay, { pointerEvents: 'none' });
                    document.body.style.overflow = '';
                }
            });
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                closeModal();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, closeModal]);

    return (
        <>
            {/* Overlay */}
            <div
                ref={overlayRef}
                className="fixed inset-0 bg-black/60 z-[1100] opacity-0 pointer-events-none backdrop-blur-sm flex items-center justify-center p-4"
                onClick={closeModal}
                aria-hidden={!isOpen}
            >
                {/* Modal Container */}
                <div
                    ref={modalRef}
                    className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[600px] max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="booking-modal-title"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-neutral-50 to-white">
                        <h2 id="booking-modal-title" className="text-xl font-semibold text-[#293039]">
                            Book an Appointment
                        </h2>
                        <button
                            onClick={closeModal}
                            className="text-neutral-400 hover:text-neutral-700 transition-colors duration-200 p-2 rounded-full hover:bg-neutral-100"
                            aria-label="Close booking form"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Form Container */}
                    <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 70px)' }}>
                        <iframe
                            width="100%"
                            height="550"
                            src="https://3ce33466.sibforms.com/serve/MUIFAPRPFCbqEI3YxPkF14Uz51ws2qGsK_tfYbp9683cuyQok1o6NG1yavqA8TefGgK4qUUHHm-10kLp4YhWVhHdYqOSK8dikUxRaBP-GxkunJ7rDSAtUU2xxVGAwtyTrlqlaAQ0_YqHOGYTTEQoo5ieGT-Kq6TvDhaUrAUxMU9QJOvwJUn_VSSveHO_EHZvBLQOgo11K462WBKF"
                            frameBorder="0"
                            scrolling="auto"
                            allowFullScreen
                            style={{ display: 'block', maxWidth: '100%', minHeight: '505px' }}
                            title="Booking Form"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default BookingModal;
